import React, { useReducer, useState, useEffect } from "react";
import { useCompanies } from "../../../api/queries/companies";
import { useSites } from "../../../api/queries/sites";
import { useSensors } from "../../../api/queries/sensors";
import Select from "react-select";

import {
  dateToFirestoreTimestamp,
  firestoreTimestampToDateString,
  handleFormErrors,
  toOptionArray,
  customSelectStyles,
} from "../../util";
import { Loading } from "../../loadingContext";

export const SensorDialog = (props) => {
  const companies = useCompanies({ activeOnly: false });
  const sites = useSites();
  const sensors = useSensors({ activeOnly: false });

  const [Site, setSite] = useState(
    props.sensor
      ? { value: props.sensor?.SiteID, label: props.sensor?.SiteName }
      : { value: null, label: "No Site" }
  );
  const [DeviceID, setDeviceID] = useState(
    props.sensor?.DeviceID || props.deviceID || ""
  );
  const [sDate, setStartDate] = useState(
    firestoreTimestampToDateString(props.sensor?.StartDate)
  );
  const id = props.id || "";

  const [errors, dispatchErrorUpdate] = useReducer(handleFormErrors, {});

  const handleSubmit = async (e) => {
    e.preventDefault();

    let isValid = true;
    // Remove site validation since "No Site" is now allowed
    if (!props.sensor && !props.deviceID && !DeviceID) {
      dispatchErrorUpdate({
        type: "set",
        field: "deviceID",
        message: "Device ID is a required field",
      });
      isValid = false;
    }
    if (!sDate) {
      dispatchErrorUpdate({
        type: "set",
        field: "startDate",
        message: "Start Date is a required field",
      });
      isValid = false;
    }

    // Validate start date is not earlier than the most recent EndDate for the selected site
    // Only enforce when explicitly requested (e.g., add form in history dialog)
    if (
      isValid &&
      props.validateAgainstSiteEndDate &&
      sensors?.data &&
      Site?.value
    ) {
      try {
        const selectedSiteId = Site?.value; // can be null for "No Site"
        const siteSensors = sensors.data
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter((s) => (selectedSiteId ? s.SiteID === selectedSiteId : !s.SiteID));

        // Find the latest end date among sensors for this site
        const latestEnd = siteSensors
          .map((s) => s.EndDate)
          .filter(Boolean)
          .map((ts) => ts.toMillis?.() ?? new Date(ts).getTime())
          .sort((a, b) => b - a)[0];

        if (latestEnd) {
          const inputStartMs = new Date(sDate).getTime();
          if (Number.isFinite(inputStartMs) && inputStartMs < latestEnd) {
            dispatchErrorUpdate({
              type: "set",
              field: "startDate",
              message: "Start Date must be after the most recent end date for this site",
            });
            isValid = false;
          }
        }
      } catch (err) {
        // Fail open on error to avoid blocking legitimate submissions, but log for debugging
        console.warn("StartDate validation against latest EndDate failed", err);
      }
    }

    if (!isValid) {
      return;
    }

    let SiteID = Site.value;
    let SiteName = Site.label;
    let StartDate = dateToFirestoreTimestamp(sDate);
    let Name = (props.sensor?.DeviceID || props.deviceID || DeviceID) + " " + (SiteName || "No Site");

    const data = {
      Name,
      SiteID,
      SiteName,
      DeviceID: props.sensor?.DeviceID || props.deviceID || DeviceID,
      StartDate,
      Active: true,
      id,
      SiteData: SiteID ? sites.data.find((doc) => doc.id === SiteID) : null,
    };

    data.DeviceID = Number(data.DeviceID);

    await props.onSubmit(data);
    props.unSetDialog();
  };

  return (
    <div>
      <form method="POST" onSubmit={handleSubmit}>
        {(companies.isLoading || sites.isLoading || sensors.isLoading) && <Loading />}
        <div className={`grid ${props.isHistoricalEntry || props.sensor || props.deviceID ? 'grid-rows-2' : 'grid-rows-4'} grid-flow-col gap-4`}>
          <label className="flex flex-col gap-1 items-center">
            <span className="block text-sm font-medium">Site</span>
            <Select
              className="input w-full max-w-xs mx-auto rounded-md p-0"
              menuPortalTarget={document.body}
              styles={customSelectStyles}
              value={Site}
              options={[
                { value: null, label: "No Site" },
                ...toOptionArray(sites.data, "Name")
              ]}
              onChange={(value) => {
                setSite(value);
                dispatchErrorUpdate({ type: "clear", field: "site" });
              }}
            ></Select>
            {errors?.site && (
              <span className="select-none text-error">{errors.site}</span>
            )}
          </label>
          {!props.isHistoricalEntry && !props.sensor && !props.deviceID && (
            <label className="flex flex-col gap-1 items-center">
              <span className="block text-sm font-medium">Device ID</span>
              <input
                type="number"
                placeholder="Device ID"
                className="input input-bordered w-full max-w-xs bg-neutral"
                value={DeviceID}
                onChange={(e) => {
                  setDeviceID(e.target.value);
                  dispatchErrorUpdate({ type: "clear", field: "deviceID" });
                }}
              />
              {errors?.deviceID && (
                <span className="select-none text-error">{errors.deviceID}</span>
              )}
            </label>
          )}
          <label className="flex flex-col gap-1 items-center">
            <span className="block text-sm font-medium">Start Date</span>
            <input
              type="date"
              placeholder="Start Date"
              className="input input-bordered w-full max-w-xs bg-neutral"
              value={sDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                dispatchErrorUpdate({ type: "clear", field: "startDate" });
              }}
            />
            {errors?.startDate && (
              <span className="select-none text-error">{errors.startDate}</span>
            )}
          </label>
        </div>
        <div className="flex flex-row gap-2 justify-center mt-4">
          <button type="submit" className="btn">
            Submit
          </button>
          <button type="button" className="btn" onClick={props.unSetDialog}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export const SensorHistoryDialog = (props) => {
  const { deviceID, unSetDialog, onEditSensor, onDeleteSensor, onNewSensor } = props;
  const sites = useSites();
  const sensors = useSensors({ activeOnly: false });
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [processedSensorGroup, setProcessedSensorGroup] = useState(null);

  // Process sensors data to get current history and latest for this device
  useEffect(() => {
    if (sensors.data && deviceID) {
      const deviceSensors = sensors.data
        .filter(sensor => sensor.data().DeviceID === deviceID)
        .map(sensor => ({ ...sensor.data(), id: sensor.id }));
      
      deviceSensors.sort((a, b) => b.StartDate.toMillis() - a.StartDate.toMillis());
      const latest = deviceSensors[0];
      const history = deviceSensors.slice(1);
      
      setProcessedSensorGroup({ latest, history });
    }
  }, [sensors.data, deviceID]);

  if (!processedSensorGroup) {
    return <Loading />;
  }

  const { latest, history } = processedSensorGroup;
  const allRows = [latest, ...history];

  const startEdit = (sensor) => {
    setEditingId(sensor.id);
    setEditData({
      SiteID: sensor.SiteID,
      SiteName: sensor.SiteName,
      StartDate: sensor.StartDate ? sensor.StartDate.toDate().toISOString().substring(0, 10) : '',
      Active: sensor.Active
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditData({});
  };

  const saveEdit = (sensor) => {
    const updatedSensor = {
      ...sensor,
      SiteID: editData.SiteID,
      SiteName: editData.SiteName,
      StartDate: dateToFirestoreTimestamp(editData.StartDate),
      Active: editData.Active,
      DeviceID: latest.DeviceID,
      id: sensor.id,
      SiteData: editData.SiteID ? sites.data.find((doc) => doc.id === editData.SiteID) : null,
    };
    onEditSensor(updatedSensor);
    setEditingId(null);
    setEditData({});
  };

  const validateStartDate = (dateString) => {
    const inputDate = new Date(dateString);
    const latestDate = latest.StartDate.toDate();
    return inputDate < latestDate;
  };

  // Removed site-based validation for historical edit rows; enforced only for add form via props

  const handleSensorUpdate = async (formData) => {
    const newEntryDate = formData.StartDate.toMillis();
    const latestSensorDate = latest.StartDate.toMillis();
    console.log(formData)

    if (newEntryDate >= latestSensorDate) {
      // This will become the new "latest" sensor
      await handleReplaceLatestSensor(formData);
    } else {
      // This is a historical entry
      await handleAddHistoricalEntry(formData);
    }
  };

  const handleReplaceLatestSensor = async (formData) => {
    // Set the current latest sensor to inactive and give it an end date
    const updatedLatest = {
      ...latest,
      Active: false,
      EndDate: formData.StartDate, // End the previous sensor when the new one starts
      SiteData: latest.SiteID ? sites.data.find((doc) => doc.id === latest.SiteID) : null,
    };
    await onEditSensor(updatedLatest);

    // Create new sensor as the active one
    const newActiveSensor = {
      ...formData,
      Active: true,
      id: '', // New sensor gets new ID
    };
    await onNewSensor(newActiveSensor);
  };

  const handleAddHistoricalEntry = async (formData) => {
    // Find the sensor that should end when this historical entry starts
    const affectedSensors = sensors.data
      .filter(sensor => sensor.data().DeviceID === deviceID)
      .map(sensor => ({ ...sensor.data(), id: sensor.id }))
      .sort((a, b) => a.StartDate.toMillis() - b.StartDate.toMillis());

    const sensorToUpdate = affectedSensors.find(sensor => {
      const sensorStart = sensor.StartDate.toMillis();
      const newStart = formData.StartDate.toMillis();
      return sensorStart > newStart;
    });

    // Create the historical entry as inactive
    const historicalSensor = {
      ...formData,
      Active: false,
      EndDate: sensorToUpdate ? sensorToUpdate.StartDate : null,
    };
    await onNewSensor(historicalSensor);
  };

  return (
    <div className="w-full max-w-4xl">
    <h3 className="font-bold text-lg p-4">Add new entry — Device {latest.DeviceID}</h3>
    <div className="border-b pb-4 mb-4">
        <SensorDialog
          onSubmit={handleSensorUpdate}
          unSetDialog={unSetDialog}
      isHistoricalEntry={true}
  validateAgainstSiteEndDate={true}
      deviceID={latest.DeviceID}
        />
      </div>

    {allRows.length > 0 && (
        <div className="overflow-x-auto w-full h-full max-h-96">
          <table className="table table-zebra table-sm">
            <thead>
              <tr>
                <th>Site</th>
                <th>Start date</th>
                {/* <th>End date</th> */}
                <th className="w-32">Actions</th>
              </tr>
            </thead>
            <tbody>
              {allRows.map((sensor) => (
                <tr key={sensor.id}>
                  <td className="align-middle">
                    <div className={`flex items-center gap-2 ${sensor.id === latest.id ? 'pl-2 border-l-4 border-primary font-semibold' : ''}`}>
                      {editingId === sensor.id ? (
                        <Select
                          className="min-w-48"
                          styles={customSelectStyles}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          value={toOptionArray(sites.data, "Name").find(s => s.value === editData.SiteID) || { value: null, label: "No Site" }}
                          options={[
                            { value: null, label: "No Site" },
                            ...toOptionArray(sites.data, "Name")
                          ]}
                          onChange={(value) => {
                            setEditData({
                              ...editData,
                              SiteID: value.value,
                              SiteName: value.label
                            });
                          }}
                        />
                      ) : (
                        <>
                          <span>{sensor.SiteName || 'No Site'}</span>
                          {sensor.id === latest.id && (
                            <span className="badge badge-primary badge-outline">Current</span>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                  <td className="w-64">
                    {editingId === sensor.id ? (
                      <input
                        type="date"
                        className="input input-sm input-bordered"
                        value={editData.StartDate}
                        onChange={(e) => {
                          setEditData({
                            ...editData,
                            StartDate: e.target.value
                          });
                        }}
                      />
                    ) : (
                      sensor.StartDate ? sensor.StartDate.toDate().toLocaleDateString("en-GB") : ''
                    )}
                  </td>
                  {/* <td>
                    {sensor.EndDate ? sensor.EndDate.toDate().toISOString().substring(0, 10) : ''}
                  </td> */}
                  <td className="w-32">
                    {editingId === sensor.id ? (
                      <div className="flex gap-1">
                        <button
                          className="btn btn-ghost btn-xs"
                          onClick={() => saveEdit(sensor)}
                          disabled={editingId !== latest.id && !validateStartDate(editData.StartDate)}
                        >
                          Save
                        </button>
                        <button
                          className="btn btn-ghost btn-xs"
                          onClick={cancelEdit}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-1">
                        <button
                          className="btn btn-ghost btn-xs"
                          onClick={() => startEdit(sensor)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-ghost btn-xs text-error"
                          onClick={() => onDeleteSensor(sensor.id)}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
